import type { CollectionStrings } from "../collection-strings";

/**
 * 繁體中文 (台灣) — collection and field labels.
 *
 * Taiwan usage throughout: 儲存 / 建立 / 搜尋 / 篩選, and 網址 rather than 链接.
 *
 * A few terms are deliberately left in English because they are the words the
 * team already uses and a translation would be less clear, not more:
 * `slug`, `og:image`, `Markdown`, and the environment name `develop`.
 */
export const zhTW: CollectionStrings = {
    article: {
        newArticle: {
            button: "新增文章",
            failed:
                "無法建立文章，資料沒有被儲存。請確認登入狀態後再試一次。",
        },
        retiredTags: (names) =>
            `無法儲存：以下標籤已封存 — ${names.join("、")}。已封存的標籤在網站上完全不會顯示：沒有標籤片、沒有篩選連結、也沒有頁面。請從這篇文章移除，或先到標籤管理解除封存。`,
        fields: {
            date: {
                name: "發布日期",
                description: "格式 2026-09-02，已預先填入今天。",
            },
            updatedAt: {
                name: "最後更新",
                description:
                    "自動維護。這個值會寫進 sitemap 的 lastmod，手動填寫等於偽造更新時間。",
            },
            slug: {
                name: "網址代稱 (slug)",
                description:
                    "網址中的片段，兩種語言共用：/news/<slug>。建立文章時會自動指定流水號，之後無法變更；先前的文章仍保留原本的 slug。",
            },
            published: {
                name: "發布狀態",
                description: "預設為關閉。草稿只會出現在 develop 預覽站。",
                onLabel: "已在網站上線",
                offLabel: "草稿 — 僅 develop 預覽可見",
            },
            tags: {
                name: "標籤",
                description: "選填。已停用的標籤不會出現在選單中。",
            },
            product: {
                name: "產品",
                description: "選填。這篇文章所屬的產品。公司消息請留空。",
            },
            img: {
                name: "主圖",
                description:
                    "選填。用於文章頁面和 og:image，列表不會顯示圖片。",
            },
            title: { name: "標題" },
            description: {
                name: "摘要",
                description:
                    "選填。作為 meta description，也會顯示在 /news 列表標題下方（最多兩行）。請自行撰寫，不會從內文擷取；留空則不會輸出 description。",
            },
            body: {
                name: "內文",
                description: "Markdown 格式，兩種語言都必填。",
            },
        },
    },
    tag: {
        newTag: {
            button: "新增標籤",
            failed:
                "無法建立標籤，資料沒有被儲存。請確認登入狀態後再試一次。",
        },
        fields: {
            date: {
                name: "建立日期",
                description: "格式 2026-09-02，已預先填入今天。",
            },
            label: {
                name: "標籤名稱",
                description:
                    "顯示在文章上的標籤文字。這屬於「內容」，不會經過程式碼的翻譯審核流程。",
            },
            category: {
                name: "分類標籤",
                description:
                    "讓這個標籤出現在最新消息頁的篩選選單。關閉時，標籤仍有自己的頁面、文章上仍會顯示，只是不作為篩選項目。",
                onLabel: "顯示於篩選",
                offLabel: "不作為篩選",
            },
            retired: {
                name: "停用",
                description:
                    "在公開網站上完全隱藏這個標籤：不顯示標籤、不出現在篩選、也不會有頁面。文章仍保留這個標籤，名稱之後也能復原。",
                onLabel: "已從網站隱藏",
                offLabel: "使用中",
            },
            order: {
                name: "排序",
                description: "選填。設定篩選列的順序，留空則依建立順序排列。",
            },
        },
    },
    streams: {
        blog: {
            article: {
                collection: {
                    name: "部落格",
                    singularName: "文章",
                    description:
                        "發布在 one-ai /blog 的文章，每篇都有自己的頁面。2026-09-16 之前發布在 /news，2026-09-17 之前發布在 /notes（Firestore 路徑仍是 notes）。",
                },
                invalidSlug: (slug) =>
                    `文章 ID「${slug}」格式不正確。只能使用小寫英文字母、數字和連字號 — 不能有空格、大寫或底線。它會成為永久網址 /blog/${slug}，之後無法變更。`,
            },
            tag: {
                collection: {
                    name: "部落格標籤",
                    singularName: "標籤",
                    description:
                        "One AI 部落格的標籤，可自由新增。",
                },
                invalidSlug: (slug) =>
                    `標籤代稱「${slug}」格式不正確。只能使用小寫英文字母、數字和連字號 — 不能有空格、大寫或底線。它會成為永久網址 /blog/tag/${slug}，之後無法變更。`,
            },
        },
        news: {
            article: {
                collection: {
                    name: "新聞",
                    singularName: "文章",
                    description:
                        "發布在 one-ai /news 的文章，每篇都有自己的頁面。建立於 2026-09-16，目前還沒有文章。",
                },
                invalidSlug: (slug) =>
                    `文章 ID「${slug}」格式不正確。只能使用小寫英文字母、數字和連字號 — 不能有空格、大寫或底線。它會成為永久網址 /news/${slug}，之後無法變更。`,
            },
            tag: {
                collection: {
                    name: "新聞標籤",
                    singularName: "標籤",
                    description:
                        "One AI 新聞的標籤，可自由新增。",
                },
                invalidSlug: (slug) =>
                    `標籤代稱「${slug}」格式不正確。只能使用小寫英文字母、數字和連字號 — 不能有空格、大寫或底線。它會成為永久網址 /news/tag/${slug}，之後無法變更。`,
            },
        },
    },
    announcements: {
        collection: {
            name: "最新消息",
            singularName: "消息",
            description: "顯示在 OneLive 首頁的 NEWS 區塊。",
        },
        fields: {
            date: { name: "日期", description: "格式 2026-05-22" },
            type: { name: "分類" },
            title: { name: "標題" },
            url: { name: "連結" },
        },
    },
    cases: {
        collection: {
            name: "合作案例",
            singularName: "案例",
            description: "顯示在 OneLive /case 頁面的卡片列表。",
        },
        fields: {
            date: { name: "日期", description: "格式 2026-06-17" },
            title: { name: "標題" },
            description: { name: "摘要" },
            img: { name: "主圖網址" },
            url: { name: "文章連結" },
        },
    },
    tagPicker: {
        edit: "選擇標籤",
        empty: "尚未選擇標籤",
        remove: (name) => `移除「${name}」`,
    },
    upload: {
        button: "上傳",
        uploading: (percent) => `上傳中… ${percent}%`,
        placeholder: "https://…",
        preview: "主圖預覽",
        errors: {
            tooLarge: (limitMb) => `圖片太大，上限為 ${limitMb} MB。`,
            notAnImage: "這個檔案不是圖片。",
            notConfigured:
                "尚未設定上傳位置（VITE_UPLOAD_ENDPOINT 未設定）。請改為貼上網址。",
            notAllowed: "上傳服務拒絕了這個登入狀態。請登出後重新登入再試一次。",
            generic: "上傳失敗。請再試一次，或改為貼上網址。",
        },
    },
    richText: {
        placeholder: "撰寫內文…",
        toolbar: {
            paragraph: "內文",
            heading2: "標題",
            heading3: "小標",
            bold: "粗體",
            italic: "斜體",
            underline: "底線",
            strikethrough: "刪除線",
            inlineCode: "程式碼",
            bulletList: "項目符號清單",
            numberedList: "編號清單",
            quote: "引言",
            codeBlock: "程式碼區塊",
            link: "連結",
            image: "插入圖片",
            embed: "插入影片",
            undo: "復原",
            redo: "重做",
        },
        link: {
            prompt: "連結網址",
            invalid:
                "請使用 https:// 開頭的網址、mailto: 信箱，或以 / 開頭的路徑。其他格式會在網站建置時被移除。",
        },
        image: {
            alt: "替代文字",
            altPlaceholder: "描述圖片內容，供螢幕閱讀器使用",
            remove: "移除",
        },
        embed: {
            prompt: "YouTube 或 Vimeo 網址",
            unsupported:
                "只能嵌入 YouTube 與 Vimeo 的影片，其他來源會在網站建置時被移除。",
            remove: "移除",
        },
        upload: {
            button: "上傳",
            uploading: (percent) => `上傳中… ${percent}%`,
            placeholder: "https://…",
            preview: "內文圖片預覽",
            errors: {
                tooLarge: (limitMb) => `圖片太大，上限為 ${limitMb} MB。`,
                notAnImage: "這個檔案不是圖片。",
                notConfigured:
                    "尚未設定上傳位置（VITE_UPLOAD_ENDPOINT 未設定）。",
                notAllowed: "上傳服務拒絕了這個登入狀態。請登出後重新登入再試一次。",
                generic: "上傳失敗。請再試一次。",
            },
        },
    },
    rebuild: {
        button: (environment) => `重新建置 ${environment}`,
        tooltip: (environment) =>
            `依目前的資料重新建置 ${environment} 網站`,
        triggered: (environments) =>
            `正在重新建置 ${environments}。建置完成後就會看到變更，通常需要一兩分鐘。`,
        allFailed: (environments) =>
            `無法啟動 ${environments} 的重新建置。資料已經儲存，只是建置沒有啟動，請用「重新建置」按鈕重試。`,
        partial: (ok, failed) =>
            `已儲存，${ok} 正在重新建置，但 ${failed} 的重新建置沒有啟動。請用「重新建置」按鈕重試。`,
    },
};
